interface TelegramWebApp {
    initData: string;
    initDataUnsafe: {
        user?: {
            id: number;
            first_name: string;
            last_name?: string;
            username?: string;
            language_code?: string;
            photo_url?: string;
            is_premium?: boolean;
        };
        start_param?: string;
    };
    HapticFeedback?: {
        impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
        notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
        selectionChanged: () => void;
    };
    viewportHeight?: number;
    expand: () => void;
    openTelegramLink?: (url: string) => void;
    openLink?: (url: string) => void;
    close?: () => void;
    contentSafeAreaInset?: {
        top: number;
        bottom: number;
        left: number;
        right: number;
    };
    onEvent: (eventType: string, eventHandler: () => void) => void;
}

declare global {
    interface Window {
        Telegram?: {
            WebApp?: TelegramWebApp;
        };
    }
}

const getInitData = () => {
    // @ts-ignore
    return window.Telegram?.WebApp?.initData || "";
};

const getTelegramId = () => {
    // @ts-ignore
    return window.Telegram?.WebApp?.initDataUnsafe?.user?.id?.toString() || "";
};

export const apiFetch = async (endpoint: string, options: RequestInit = {}) => {
    const headers = new Headers(options.headers || {});

    // Add Authentication Headers
    const initData = getInitData();
    if (initData) {
        headers.append('x-telegram-init-data', initData);
        // Fallback standard Authorization header for proxies that strip custom X- headers
        headers.append('Authorization', `tma ${initData}`);
    }

    // For older admin checks that rely on ID directly (less secure, but kept for compatibility validation)
    const tgId = getTelegramId();
    if (tgId) {
        headers.append('x-telegram-id', tgId);
    }

    // Default to JSON if body is object and not FormData
    if (options.body && typeof options.body === 'string' && !headers.has('Content-Type')) {
        try {
            JSON.parse(options.body);
            headers.append('Content-Type', 'application/json');
        } catch (e) {
            // Not JSON
        }
    } else if (options.body && !(options.body instanceof FormData) && !headers.has('Content-Type')) {
        headers.append('Content-Type', 'application/json');
    }

    const config = {
        ...options,
        headers,
    };

    const response = await fetch(endpoint, config);

    if (response.status === 401 || response.status === 403) {
        // Only clear session if it's an explicit JSON auth error from our API, 
        // to prevent Cloudflare/VPN 403s from logging the user out.
        const contentType = response.headers.get('content-type');
        if (response.status === 401 || (contentType && contentType.includes('application/json'))) {
            console.warn(`[API] Auth failed (${response.status}) on ${endpoint}. Session may be expired.`);
            // Clear stale user cache so next re-open fetches fresh data
            try {
                localStorage.removeItem('cache_user_data');
                localStorage.removeItem('cache_user_ts');
            } catch (_) {}
        }
    }

    return response;
};

// --- User Endpoints ---
export const userApi = {
    sync: (data: any) => apiFetch('/api/users/sync', { method: 'POST', body: JSON.stringify(data) }),
    get: (id: string | number) => apiFetch(`/api/users/${id}`),
    getPublicProfile: (id: string | number) => apiFetch(`/api/users/public/${id}`),
    getUsernames: (id: string | number) => apiFetch(`/api/users/${id}/usernames`),
    getTransactions: (id: string | number) => apiFetch(`/api/users/${id}/transactions`),
    updateProfile: (id: string | number, data: any) => apiFetch(`/api/users/update/${id}`, { method: 'POST', body: JSON.stringify(data) }),
    getLikes: (id: string | number) => apiFetch(`/api/users/${id}/likes`),
    getActive: () => apiFetch('/api/users/active'),
    getPushUsernames: () => apiFetch('/api/users/push-usernames'),
    getPushAccounts: () => apiFetch('/api/users/push-accounts'),
    updatePushAccount: (formData: FormData) => apiFetch('/api/users/push-accounts/update', { method: 'POST', body: formData }),
    getLeaderboard: () => apiFetch('/api/users/leaderboard'),
    detectKind: (data: { username: string }) => apiFetch('/api/users/detect-kind', { method: 'POST', body: JSON.stringify(data) }),
};

// --- Admin Endpoints ---
export const adminApi = {
    getSystemStats: () => apiFetch('/api/admin/stats'),
    getDatabaseStats: () => apiFetch('/api/admin/database-stats'),
    truncateTable: (tableName: string) => apiFetch('/api/admin/database/truncate', { method: 'POST', body: JSON.stringify({ tableName }) }),
    getTableData: (tableName: string) => apiFetch(`/api/admin/database/table/${tableName}`),
    getAuctionMonitor: () => apiFetch('/api/admin/auctions/monitor'),
    getFinancialAudit: () => apiFetch('/api/admin/financials/audit'),
    getAllUsers: () => apiFetch('/api/admin/users'),
    unfreezeFunds: (data: any) => apiFetch('/api/admin/funds/unfreeze', { method: 'POST', body: JSON.stringify(data) }),
    getPackages: () => apiFetch('/api/admin/packages'),
    createPackage: (formData: FormData) => apiFetch('/api/admin/packages', { method: 'POST', body: formData }),
    getPackageCards: (id: number) => apiFetch(`/api/admin/packages/${id}/cards`),
    deletePackage: (id: number) => apiFetch(`/api/admin/packages/${id}`, { method: 'DELETE' }),
    deleteCard: (id: number) => apiFetch(`/api/admin/cards/${id}`, { method: 'DELETE' }),
    getProfitStats: () => apiFetch('/api/admin/financials/profit-stats'),
    getMonitoringLogs: () => apiFetch('/api/admin/monitoring/logs'),
    getRelatedLogs: (params: { dealId?: any; listingId?: any; cardId?: any }) => {
        const query = new URLSearchParams(params as any).toString();
        return apiFetch(`/api/admin/monitoring/related?${query}`);
    },
    getDisputes: () => apiFetch('/api/admin/disputes'),
    resolveDispute: (data: { dealId: number; resolution: 'refund' | 'release'; adminReason: string }) => apiFetch('/api/admin/resolve-dispute', { method: 'POST', body: JSON.stringify(data) }),
    getRecordsStats: () => apiFetch('/api/admin/records/stats'),
    getSalesRecords: (params: { type: string; status?: string; assetType?: string }) => {
        const query = new URLSearchParams(params as any).toString();
        return apiFetch(`/api/admin/records/sales?${query}`);
    },
    getSubscriberStats: () => apiFetch('/api/admin/records/subscribers'),
    // Custom UI Config
    getAnimationConfig: () => apiFetch('/api/admin/animation-config'),
    setLogoText: (text: string) => apiFetch('/api/admin/logo-text', { method: 'POST', body: JSON.stringify({ text }) }),
    setProfileTileBgs: (backgrounds: any) => apiFetch('/api/admin/profile-tiles-bg', { method: 'POST', body: JSON.stringify({ backgrounds }) }),
    setAnimationConfig: (enabled: boolean) => apiFetch('/api/admin/animation-config', { method: 'POST', body: JSON.stringify({ enabled }) }),
    uploadUIAsset: (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        return apiFetch('/api/admin/upload-ui', { method: 'POST', body: formData });
    },
    // Gifting & Analytics
    searchUsers: (query: string) => apiFetch(`/api/admin/users/search?query=${encodeURIComponent(query)}`),
    giftSubscription: (data: any) => apiFetch('/api/admin/gift/subscription', { method: 'POST', body: JSON.stringify(data) }),
    giftCard: (data: any) => apiFetch('/api/admin/gift/card', { method: 'POST', body: JSON.stringify(data) }),
    giftAll: (data: { itemType: 'card' | 'skin' | 'package'; itemId: string | number }) => apiFetch('/api/admin/gift/all', { method: 'POST', body: JSON.stringify(data) }),
    getCardAnalytics: () => apiFetch('/api/admin/cards/analytics'),
    getAssetsList: () => apiFetch('/api/admin/assets/list'),
};

// --- P2P Endpoints ---
export const p2pApi = {
    requestDeal: (data: any) => apiFetch('/api/p2p/request', { method: 'POST', body: JSON.stringify(data) }),
    getActive: () => apiFetch('/api/p2p/active'),
    getHistory: (userId?: string | number) => apiFetch(`/api/p2p/history${userId ? `?userId=${userId}` : ''}`),
    getHistoryDetail: (id: number) => apiFetch(`/api/p2p/history/${id}`),
    confirmDeal: (dealId: number, userId?: string | number, role?: string) => apiFetch(`/api/p2p/${dealId}/confirm`, { method: 'POST', body: JSON.stringify({ userId, role }) }),
    getChat: (dealId: number) => apiFetch(`/api/p2p/${dealId}/chat`),
    sendMessage: (dealId: number, message: string, userId?: string | number) => apiFetch(`/api/p2p/${dealId}/chat`, { method: 'POST', body: JSON.stringify({ message, userId }) }),
    checkBot: (dealId: number) => apiFetch(`/api/p2p/${dealId}/check-bot`),
    getDeal: (dealId: number) => apiFetch(`/api/p2p/deal/${dealId}`),
    verifyDeal: (dealId: number) => apiFetch(`/api/p2p/${dealId}/verify`, { method: 'POST' }),
    closeDeal: (dealId: number) => apiFetch(`/api/p2p/${dealId}/close`, { method: 'POST' }),
    disputeDeal: (dealId: number, userId?: string | number, reason?: string) => apiFetch(`/api/p2p/${dealId}/dispute`, { method: 'POST', body: JSON.stringify({ userId, reason }) }),
    getAllContracts: () => apiFetch('/api/p2p/contracts'),
};

// --- Store/Items Endpoints ---
export const storeApi = {
    listUsername: (data: any) => apiFetch('/api/store/list', { method: 'POST', body: JSON.stringify(data) }),
    cancelListing: (cardId: number) => apiFetch('/api/store/cancel', { method: 'POST', body: JSON.stringify({ cardId }) }),
    removeAsset: (cardId: string | number, assetType?: string) => apiFetch('/api/store/remove-asset', { method: 'POST', body: JSON.stringify({ cardId, assetType }) }),
    cancelByCard: (cardId: number, userId?: string | number) => apiFetch('/api/store/cancel', { method: 'POST', body: JSON.stringify({ cardId, userId }) }),
    buyListing: (cardId: string, userId: string | number) => apiFetch(`/api/store/buy`, { method: 'POST', body: JSON.stringify({ cardId, userId }) }),
    incrementShare: (id: string) => apiFetch(`/api/store/cards/${id}/share`, { method: 'POST' }),
    mintNft: (data: any) => apiFetch('/api/store/mint-nft', { method: 'POST', body: JSON.stringify(data) }),
    getListings: (userId?: string | number, page: number = 1, limit: number = 50, filter?: string) => {
        const params = new URLSearchParams();
        if (userId) params.append('userId', String(userId));
        params.append('page', String(page));
        params.append('limit', String(limit));
        if (filter) params.append('filter', filter);
        return apiFetch(`/api/store/listings?${params.toString()}`);
    },
    getListingDetails: (id: string | number) => apiFetch(`/api/store/listing/${id}`),
    getPublicCard: (id: string, userId?: string | number) => apiFetch(`/api/store/cards/${id}${userId ? `?userId=${userId}` : ''}`),

    // Items Routes
    getMarketItems: (filter?: string, userId?: string, packageId?: string | number, signal?: AbortSignal) => {
        const params = new URLSearchParams();
        if (filter) params.append('filter', filter);
        if (userId) params.append('userId', userId);
        if (packageId) params.append('packageId', String(packageId));
        return apiFetch(`/api/items/market?${params.toString()}`, { signal });
    },
    buyItemDirect: (data: any) => apiFetch('/api/items/buy', { method: 'POST', body: JSON.stringify(data) }),
    sellItemDirect: (data: any) => apiFetch('/api/items/sell', { method: 'POST', body: JSON.stringify(data) }),
};

// --- Auction Endpoints ---
export const auctionApi = {
    getActive: (userId?: string | number, filter?: string) => {
        let url = `/api/auctions`;
        const params = new URLSearchParams();
        if (userId) params.append('userId', userId.toString());
        if (filter) params.append('filter', filter);
        const query = params.toString();
        if (query) url += `?${query}`;
        return apiFetch(url);
    },
    getFinished: (userId?: string | number) => apiFetch(`/api/auctions/finished${userId ? `?userId=${userId}` : ''}`),
    create: (data: any) => apiFetch('/api/auctions/create', { method: 'POST', body: JSON.stringify(data) }),
    bid: (auctionId: string | number, bidAmount: number, userId?: string | number) => apiFetch('/api/auctions/bid', { method: 'POST', body: JSON.stringify({ auctionId, bidAmount, userId }) }),
    cancel: (auctionId: string | number, userId?: string | number) => apiFetch('/api/auctions/cancel', { method: 'POST', body: JSON.stringify({ auctionId, userId }) }),
    cancelByCard: (cardId: number) => apiFetch('/api/auctions/cancel-by-card', { method: 'POST', body: JSON.stringify({ cardId }) }),
    resolve: (id: string | number, userId?: string | number) => apiFetch(`/api/auctions/${id}/resolve`, { method: 'POST', body: JSON.stringify({ userId }) }),
    getDetails: (id: string | number, viewerId?: string) => apiFetch(`/api/auctions/${id}${viewerId ? `?viewerId=${viewerId}` : ''}`),
};

// --- Wallet Endpoints ---
export const walletApi = {
    connect: (data: any) => apiFetch('/api/wallet/connect', { method: 'POST', body: JSON.stringify(data) }),
    deposit: (amount: number, hash: string) => apiFetch('/api/wallet/deposit', { method: 'POST', body: JSON.stringify({ amount, hash }) }),
    withdraw: (data: any) => apiFetch('/api/wallet/withdraw', { method: 'POST', body: JSON.stringify(data) }),
    scanDeposits: () => apiFetch('/api/wallet/scan-deposits', { method: 'POST' }),
    getSystemAddress: () => apiFetch('/api/wallet/address'),
    checkNft: (data: any) => apiFetch('/api/wallet/check-nft', { method: 'POST', body: JSON.stringify(data) }),
    getUserNfts: (userId: string | number) => apiFetch(`/api/wallet/my-nfts/${userId}`),
    getNftEvent: (eventId: string) => apiFetch(`/api/wallet/event-details/${eventId}`),
    withdrawNft: (data: any) => apiFetch('/api/wallet/withdraw-nft', { method: 'POST', body: JSON.stringify(data) }),
};

// --- Level Endpoints ---
export const levelApi = {
    getAll: () => apiFetch('/api/levels'),
    getProgress: (userId: string | number) => apiFetch(`/api/levels/progress/${userId}`),
    upgrade: (data: any) => apiFetch('/api/levels/upgrade', { method: 'POST', body: JSON.stringify(data) }),
    buyXP: (amount: number) => apiFetch('/api/levels/buy-xp', { method: 'POST', body: JSON.stringify({ amount }) }),
    updateLevel: (levelId: number, data: any) => apiFetch(`/api/levels/update/${levelId}`, { method: 'PUT', body: JSON.stringify(data) }),
    upload: (formData: FormData) => apiFetch('/api/levels/upload', { method: 'POST', body: formData }),
};

// --- Notification Endpoints ---
export const notificationApi = {
    getAll: (userId?: string | number) => apiFetch(`/api/notifications${userId ? `?userId=${userId}` : ''}`),
    getUnread: () => apiFetch('/api/notifications/unread'),
    markRead: (ids?: number[], userId?: string | number) => apiFetch('/api/notifications/mark-read', { method: 'POST', body: JSON.stringify({ ids, userId }) }),
};

// --- Referral Endpoints ---
export const referralApi = {
    getStats: (userId: string | number) => apiFetch(`/api/referrals/${userId}`),
    check: (referrerId: string) => apiFetch('/api/referrals/check', { method: 'POST', body: JSON.stringify({ referrerId }) }),
};

// --- Card Endpoints ---
export const cardApi = {
    getUserCards: (userId: string | number) => apiFetch(`/api/users/${userId}/cards`),
    getAllCards: () => apiFetch('/api/cards'),
    getByUuid: (uuid: string) => apiFetch(`/api/cards/${uuid}`),
    getDetails: (id: string | number, userId?: string | number) => apiFetch(`/api/cards/${id}${userId ? `?userId=${userId}` : ''}`),
    claimStarter: (userId: string | number) => apiFetch('/api/cards/claim-starter', { method: 'POST', body: JSON.stringify({ userId }) }),
    getFree: () => apiFetch('/api/cards/free'),
    claim: (data: any) => apiFetch('/api/cards/claim', { method: 'POST', body: JSON.stringify(data) }),
    buy: (data: any) => apiFetch('/api/cards/buy', { method: 'POST', body: JSON.stringify(data) }),
    like: (cardId: number, userId: string | number) => apiFetch('/api/cards/like', { method: 'POST', body: JSON.stringify({ cardId, userId }) }),
};

// --- Ads Endpoints ---
export const adApi = {
    getConfig: () => apiFetch('/api/ads/config'),
    updateConfig: (data: any) => apiFetch('/api/ads/config', { method: 'POST', body: JSON.stringify(data) }),
    upload: (formData: FormData) => apiFetch('/api/ads/upload', { method: 'POST', body: formData }),
};

// --- Bot Wallet Endpoints ---
export const botWalletApi = {
    getStats: () => apiFetch('/api/bot-wallet/stats'),
    withdraw: (data: any) => apiFetch('/api/bot-wallet/withdraw', { method: 'POST', body: JSON.stringify(data) }),
};

// --- Skin Endpoints ---
export const skinApi = {
    getAll: () => apiFetch('/api/skins'),
    buy: (skinId: number) => apiFetch('/api/skins/buy', { method: 'POST', body: JSON.stringify({ skinId }) }),
    equip: (skinId: number) => apiFetch('/api/skins/equip', { method: 'POST', body: JSON.stringify({ skinId }) }),
    add: (data: any) => apiFetch('/api/skins/add', { method: 'POST', body: JSON.stringify(data) }),
    getEquipped: () => apiFetch('/api/skins/equipped'),
    getUserSkins: (id: string | number) => apiFetch(`/api/skins/user/${id}`),
};

export const homeApi = {
    getSections: (userId?: string | number) => apiFetch(`/api/home/sections${userId ? `?userId=${userId}` : ''}`),
    getUserStats: () => apiFetch('/api/home/user-stats'),
    getTopSellers: (userId?: string | number, side: 'sell' | 'buy' = 'sell') => apiFetch(`/api/home/top-sellers${userId ? `?userId=${userId}` : ''}${side ? `${userId ? '&' : '?'}side=${side}` : ''}`),
    getBuyerUsernames: (userId: string | number) => apiFetch(`/api/home/top-sellers/${encodeURIComponent(String(userId))}/usernames`),
    getRecentlySold: () => apiFetch('/api/home/recently-sold'),
    getLogoText: () => apiFetch('/api/home/logo-text'),
    getProfileTileBgs: () => apiFetch('/api/home/profile-tiles-bg'),
};

// --- Username Market Endpoints (unified: store / auction / offers) ---
export const usernameMarketApi = {
    market: (type: string = 'all') => apiFetch(`/api/username-market/market?type=${type}`),
    valuation: (username: string, force = false) => apiFetch(`/api/username-market/valuation/${encodeURIComponent(String(username).replace(/^@/, ''))}${force ? '?force=1' : ''}`),
    my: () => apiFetch('/api/username-market/my'),
    createListing: (data: any) => apiFetch('/api/username-market/listings', { method: 'POST', body: JSON.stringify(data) }),
    createOffer: (data: any) => apiFetch('/api/username-market/offers', { method: 'POST', body: JSON.stringify(data) }),
    offerAction: (id: number, data: any) => apiFetch(`/api/username-market/offers/${id}/action`, { method: 'POST', body: JSON.stringify(data) }),
    placeBid: (listingId: number, bidAmount: number) => apiFetch('/api/username-market/bids', { method: 'POST', body: JSON.stringify({ listingId, bidAmount }) }),
    auctionDetail: (listingId: number) => apiFetch(`/api/username-market/auctions/${listingId}`),
    offersForAsset: (assetId: string | number) => apiFetch(`/api/username-market/offers/asset/${assetId}`),
    buy: (listingId: number) => apiFetch('/api/username-market/buy', { method: 'POST', body: JSON.stringify({ listingId }) }),
    transfer: (id: number, method: string, side: string) => apiFetch(`/api/username-market/orders/${id}/transfer`, { method: 'POST', body: JSON.stringify({ method, side }) }),
    order: (id: number) => apiFetch(`/api/username-market/orders/${id}`),
     myPurchases: () => apiFetch('/api/username-market/my-purchases'),
     p2pStart: (orderId: number) => apiFetch(`/api/username-market/p2p-deal/${orderId}/start`, { method: 'POST' }),
     p2pGet: (dealId: number) => apiFetch(`/api/username-market/p2p-deal/${dealId}`),
     p2pCheckBot: (dealId: number) => apiFetch(`/api/username-market/p2p-deal/${dealId}/check-bot`, { method: 'POST' }),
     p2pVerify: (dealId: number) => apiFetch(`/api/username-market/p2p-deal/${dealId}/verify`, { method: 'POST' }),
     p2pConfirm: (dealId: number, role: string, confirmed: boolean) => apiFetch(`/api/username-market/p2p-deal/${dealId}/confirm`, { method: 'POST', body: JSON.stringify({ role, confirmed }) }),
     p2pCancel: (dealId: number, reason?: string) => apiFetch(`/api/username-market/p2p-deal/${dealId}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),
     p2pDispute: (dealId: number, reason: string) => apiFetch(`/api/username-market/p2p-deal/${dealId}/dispute`, { method: 'POST', body: JSON.stringify({ reason }) }),
     p2pMessage: (dealId: number, text: string) => apiFetch(`/api/username-market/p2p-deal/${dealId}/messages`, { method: 'POST', body: JSON.stringify({ text }) }),
     p2pReport: (data: FormData) => apiFetch('/api/username-market/review-report', { method: 'POST', body: data }),
     appOrders: () => apiFetch('/api/username-market/app-orders'),
     appOrder: (id: number) => apiFetch(`/api/username-market/app-order/${id}`),
    appOrderCancel: (id: number, reason?: string) => apiFetch(`/api/username-market/app-order/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),
    appOrderWithdraw: (id: number, target?: string, botConfirmed?: boolean, targetType?: 'id' | 'username') => apiFetch(`/api/username-market/app-order/${id}/withdraw`, { method: 'POST', body: JSON.stringify({ target: target || undefined, bot_confirmed: botConfirmed || false, target_type: targetType }) }),
    appOrderRetry: (id: number) => apiFetch(`/api/username-market/app-order/${id}/retry`, { method: 'POST' }),
 };
